// El bloque «Documentos» del filtro de Personal (S02 R11, R12; P15-I19): sus opciones, sus
// tooltips, los parámetros que escribe en BaseGetsListado y lo que dice en «Filtro Aplicado».
//
// Módulo puro, para probarlo con Jest sin montar el modal (Periodo.select.vue). El modal lo usa
// sólo con la prop mostrarContratos, que únicamente enciende Personal/Listado.vue (E47).
//
// Los cuatro parámetros viven en ColaboradoresGets_Model (Lib.V6Base) y el backend los filtra en
// ColaboradoresController.Contratos.cs (FiltraPorContrato). Sin Contratos.Listar, cualquiera de
// ellos con valor responde 403 "FiltroContrato" (PermisoFiltroContratoFilter, S10 R11): por eso el
// modal los quita cuando no muestra el bloque (R11, E74).
//
// Las etiquetas «Vigente», «Por vencer (30 días)», «Vencido» y «Sin contrato» son las del grupo
// ContratoEstados que devuelve Filtro/Listado (R10); «Cualquiera» y «Sin contrato vigente» las
// agrega el frontend. Las opciones de «Firma» y sus nombres vienen de S08 R20 (firma-contrato.js).

import { FILTRO_FIRMA, EstadosFirmaDeFiltro } from "@/util/firma-contrato";

// Clave del grupo de Filtro/Listado (R10) y nombre del parámetro (ColaboradoresController.GrupoContratoEstados).
export const GRUPO_CONTRATO_ESTADOS = "ContratoEstados";

// Los parámetros de contrato de ColaboradoresGets_Model, en el orden de PermisoFiltroContratoFilter.
export const PARAMETROS_CONTRATO = Object.freeze(["ContratoEstados", "ContratoDesde", "ContratoHasta", "EstadosFirma"]);

export const CUALQUIERA = "Cualquiera";
export const SIN_CONTRATO_VIGENTE = "SinContratoVigente";

// «Sin contrato vigente» no es el complemento de «Vigente» (Definiciones, D09.17): SinContrato + Vencido.
const ESTADOS_SIN_CONTRATO_VIGENTE = "SinContrato,Vencido";

// Tooltips de R11 (el de «Firma» es de S02; S08 R20 lo cita).
export const TIP_POR_VENCER = "El contrato más reciente ya empezó y termina entre hoy y dentro de 30 días";
export const TIP_SIN_CONTRATO_VIGENTE =
  "Personas sin contratos o cuyo contrato más reciente ya venció. No incluye a quien tiene hoy un contrato cancelado o con error, ni a quien espera una renovación que aún no empieza";
export const TIP_FIRMA =
  "Incluye a quien tenga algún contrato en ese estado, aunque no sea el más reciente. Si eliges fechas de contrato, ese mismo contrato debe caer en ellas.";
export const TIP_FECHAS = "Personas con algún contrato que cruce este rango, aunque empiece antes o termine después";

const TOOLTIPS_CONTRATO = Object.freeze({
  PorVencer: TIP_POR_VENCER,
  [SIN_CONTRATO_VIGENTE]: TIP_SIN_CONTRATO_VIGENTE,
});

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// El estado del bloque: «Contrato» y «Firma» en «Cualquiera», fechas vacías (E45).
export function DocumentosVacios() {
  return { Contrato: CUALQUIERA, Firma: CUALQUIERA, Fechas: null };
}

// Copia, para que el estado guardado (PeriodoValoresGuarda) no comparta el arreglo de fechas con el modal.
export function CopiaDocumentos(Documentos) {
  const R = DocumentosVacios();
  if (Documentos == null) return R;
  if (Documentos.Contrato) R.Contrato = Documentos.Contrato;
  if (Documentos.Firma) R.Firma = Documentos.Firma;
  if (Array.isArray(Documentos.Fechas) && Documentos.Fechas.length == 2) R.Fechas = Documentos.Fechas.slice();
  return R;
}

export function TieneDocumentos(Documentos) {
  if (Documentos == null) return false;
  return (
    (Documentos.Contrato != null && Documentos.Contrato !== CUALQUIERA) ||
    (Documentos.Firma != null && Documentos.Firma !== CUALQUIERA) ||
    TieneFechas(Documentos.Fechas)
  );
}

function TieneFechas(Fechas) {
  return Array.isArray(Fechas) && Fechas.length == 2 && !!Fechas[0] && !!Fechas[1];
}

// ¿Es el grupo ContratoEstados de la respuesta de Filtro/Listado? (el id llega tal cual, R10)
export function EsGrupoContratos(Filtro) {
  return Filtro != null && typeof Filtro.id === "string" && Filtro.id.toLowerCase() === GRUPO_CONTRATO_ESTADOS.toLowerCase();
}

/**
 * Las opciones del control «Contrato»: «Cualquiera», los valores del grupo en su orden y con su
 * etiqueta, y «Sin contrato vigente».
 * @param {{ datos: Array<{ id: string, etiqueta: string }> }} Grupo el grupo ContratoEstados
 * @returns {Array<{ valor: string, nombre: string, tooltip?: string }>}
 */
export function OpcionesContrato(Grupo) {
  const R = [{ valor: CUALQUIERA, nombre: CUALQUIERA }];
  const Datos = Grupo != null && Array.isArray(Grupo.datos) ? Grupo.datos : [];
  Datos.forEach((D) => R.push(ConTooltip({ valor: D.id, nombre: D.etiqueta || D.id })));
  R.push(ConTooltip({ valor: SIN_CONTRATO_VIGENTE, nombre: "Sin contrato vigente" }));
  return R;
}

function ConTooltip(Opcion) {
  if (TOOLTIPS_CONTRATO[Opcion.valor]) Opcion.tooltip = TOOLTIPS_CONTRATO[Opcion.valor];
  return Opcion;
}

// Las opciones del control «Firma» (S08 R20): «Cualquiera», «Por firmar» y «Firmado».
export function OpcionesFirma() {
  return [{ valor: CUALQUIERA, nombre: CUALQUIERA }].concat(
    FILTRO_FIRMA.map((O) => ({ valor: O.valor, nombre: O.nombre }))
  );
}

// El valor de ContratoEstados para una opción del control «Contrato», o null («Cualquiera»).
export function EstadosContratoDeOpcion(Valor) {
  if (Valor == null || Valor === "" || Valor === CUALQUIERA) return null;
  if (Valor === SIN_CONTRATO_VIGENTE) return ESTADOS_SIN_CONTRATO_VIGENTE;
  return Valor;
}

// Quita los cuatro parámetros de contrato. Devuelve true si había alguno.
export function QuitaParametrosContrato(Parametros) {
  if (Parametros == null) return false;
  let Habia = false;
  PARAMETROS_CONTRATO.forEach((P) => {
    if (Object.prototype.hasOwnProperty.call(Parametros, P)) {
      Habia = true;
      delete Parametros[P];
    }
  });
  return Habia;
}

/**
 * Escribe en `Parametros` (el BaseGetsListado que emite el modal) lo que pide el bloque. «Cualquiera»
 * y el rango vacío **borran** la llave: no se manda cadena vacía (plan, «Frontend»).
 */
export function AplicaDocumentos(Parametros, Documentos) {
  QuitaParametrosContrato(Parametros);
  if (Documentos == null) return Parametros;
  const Estados = EstadosContratoDeOpcion(Documentos.Contrato);
  if (Estados) Parametros.ContratoEstados = Estados;
  const Firmas = EstadosFirmaDeFiltro(Documentos.Firma);
  if (Firmas) Parametros.EstadosFirma = Firmas;
  if (TieneFechas(Documentos.Fechas)) {
    Parametros.ContratoDesde = Documentos.Fechas[0];
    Parametros.ContratoHasta = Documentos.Fechas[1];
  }
  return Parametros;
}

// «2026-11-01» → «01/nov/2026», como los tooltips del indicador.
export function FechaCorta(Iso) {
  const M = /^(\d{4})-(\d{2})-(\d{2})/.exec(Iso || "");
  if (!M) return Iso || "";
  return `${M[3]}/${MESES[Number(M[2]) - 1]}/${M[1]}`;
}

/**
 * Lo que «Filtro Aplicado» dice del bloque (R12, E57): cada control que no está en «Cualquiera» o vacío.
 * @returns {string[]} p. ej. ["Contrato: Sin contrato vigente", "Firma: Por firmar", "Contratos del 01/nov/2026 al 30/nov/2026"]
 */
export function TextoDocumentos(Documentos, OpcionesDeContrato) {
  const R = [];
  if (Documentos == null) return R;
  if (Documentos.Contrato && Documentos.Contrato !== CUALQUIERA) {
    const O = (OpcionesDeContrato || []).find((X) => X.valor === Documentos.Contrato);
    R.push(`Contrato: ${O ? O.nombre : Documentos.Contrato}`);
  }
  if (Documentos.Firma && Documentos.Firma !== CUALQUIERA) {
    const O = OpcionesFirma().find((X) => X.valor === Documentos.Firma);
    R.push(`Firma: ${O ? O.nombre : Documentos.Firma}`);
  }
  if (TieneFechas(Documentos.Fechas))
    R.push(`Contratos del ${FechaCorta(Documentos.Fechas[0])} al ${FechaCorta(Documentos.Fechas[1])}`);
  return R;
}

export default {
  GRUPO_CONTRATO_ESTADOS,
  PARAMETROS_CONTRATO,
  CUALQUIERA,
  SIN_CONTRATO_VIGENTE,
  TIP_POR_VENCER,
  TIP_SIN_CONTRATO_VIGENTE,
  TIP_FIRMA,
  TIP_FECHAS,
  DocumentosVacios,
  CopiaDocumentos,
  TieneDocumentos,
  EsGrupoContratos,
  OpcionesContrato,
  OpcionesFirma,
  EstadosContratoDeOpcion,
  QuitaParametrosContrato,
  AplicaDocumentos,
  FechaCorta,
  TextoDocumentos,
};
